import { z } from 'zod';
import { reauthenticateUrlSchema } from './public-auth.js';
export * from './public-auth.js';
export * from './public-frontend.js';
export * from './canteen.js';
export * from './canteen-openapi.js';
export * from './canteen-operations.js';
export * from './canteen-menu.js';
export * from './canteen-dietary.js';
export * from './canteen-delegations.js';
export * from './receivables.js';
export * from './canteen-reports.js';
export * from './cards.js';
export * from './card-reader.js';
export * from './dms.js';
export * from './communication.js';
export * from './notifications.js';
export * from './canteen-communication-notifications.js';
export * from './communication-notifications-openapi.js';
export * from './product-clients.js';
export * from './workflow.js';
export * from './legacy-bridge.js';
export const identifierSchema = z.string().uuid();
export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const accountStateSchema = z.enum([
    'invited',
    'active',
    'blocked',
    'suspended',
    'expired',
    'closed',
    'anonymized',
    'archived',
]);
export const provisioningStateSchema = z.enum([
    'pending',
    'processing',
    'ready',
    'failed',
]);
export const businessProfileTypeSchema = z.enum([
    'basic',
    'candidate',
    'student',
    'guardian',
    'employee',
    'teacher',
    'boarding_resident',
    'meal_recipient',
    'administrator',
]);
export const businessProfileSchema = z.object({
    id: identifierSchema,
    type: businessProfileTypeSchema,
    status: z.enum(['active', 'withdrawn']),
    validFrom: isoDateTimeSchema,
    validUntil: isoDateTimeSchema.nullable(),
    revision: z.number().int().positive(),
});
export const grantBusinessProfileSchema = z
    .object({
    type: businessProfileTypeSchema,
    validFrom: isoDateTimeSchema.optional(),
    validUntil: isoDateTimeSchema.nullable().optional(),
})
    .refine((value) => !value.validFrom ||
    !value.validUntil ||
    new Date(value.validUntil) > new Date(value.validFrom), { message: 'Koniec profilu musi być późniejszy od początku.' });
export const withdrawBusinessProfileSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const userSummarySchema = z.object({
    id: identifierSchema,
    displayName: z.string().min(1).max(200),
    /** Brief public administrative context, suitable for an authorised user list. */
    description: z.string().max(200).nullable().default(null),
    email: z.email().nullable(),
    state: accountStateSchema,
    provisioningState: provisioningStateSchema,
    provisioningError: z.string().nullable(),
    groupIds: z.array(identifierSchema),
    lastLoginAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    redactedFields: z.array(z.enum(['email', 'lastLoginAt', 'provisioningError'])),
});
export const userPageSchema = z.object({
    items: z.array(userSummarySchema),
    nextOffset: z.number().int().nonnegative().nullable(),
    total: z.number().int().nonnegative(),
    redactedFields: userSummarySchema.shape.redactedFields,
});
export const userDetailsSchema = userSummarySchema.extend({
    /** Stable person identity used by domain applications; `id` is the access account. */
    personId: identifierSchema,
    givenName: z.string().min(1).max(100),
    familyName: z.string().min(1).max(100),
    locale: z.string().min(2).max(20),
    revision: z.number().int().positive(),
    profiles: z.array(businessProfileSchema),
});
export const updatePersonDescriptionSchema = z.object({
    description: z.string().trim().max(200).nullable(),
    revision: z.number().int().positive(),
});
export const personAdministrativeNoteRevisionSchema = z.object({
    revision: z.number().int().positive(),
    body: z.string().min(1).max(2000),
    editedByAccountId: identifierSchema,
    editedByDisplayName: z.string().min(1).max(200),
    editedAt: isoDateTimeSchema,
});
export const personAdministrativeNoteSchema = z.object({
    id: identifierSchema,
    personId: identifierSchema,
    revision: z.number().int().positive(),
    body: z.string().min(1).max(2000),
    authorAccountId: identifierSchema,
    authorDisplayName: z.string().min(1).max(200),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    history: z.array(personAdministrativeNoteRevisionSchema),
});
export const createPersonAdministrativeNoteSchema = z.object({
    body: z.string().trim().min(1).max(2000),
});
export const updatePersonAdministrativeNoteSchema = z.object({
    body: z.string().trim().min(1).max(2000),
    revision: z.number().int().positive(),
});
export const currentPrincipalSchema = z.object({
    accountId: identifierSchema,
    personId: identifierSchema,
    institutionId: identifierSchema,
    displayName: z.string().min(1).max(200),
    profileTypes: z.array(businessProfileTypeSchema),
    allowedOperations: z.array(z.string()),
});
/**
 * A short-lived, opaque proof is issued only after IAM receives a fresh OIDC
 * authentication result.  The browser never gets to assert its own time.
 */
export const reauthenticationChallengeRequestSchema = z.object({
    audience: z.literal('communication-api'),
    action: z.literal('communication.critical_access'),
});
export const reauthenticationChallengeSchema = z.object({
    reauthenticateUrl: reauthenticateUrlSchema,
    expiresAt: isoDateTimeSchema,
});
export const reauthenticationProofConsumeRequestSchema = z.object({
    audience: z.literal('communication-api'),
    action: z.literal('communication.critical_access'),
    proof: z.string().min(32).max(200),
});
export const reauthenticationProofReceiptSchema = z.object({
    institutionId: identifierSchema,
    accountId: identifierSchema,
    personId: identifierSchema,
    audience: z.literal('communication-api'),
    action: z.literal('communication.critical_access'),
    reauthenticatedAt: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema,
});
export const directoryAudienceRequestSchema = z
    .object({
    mode: z.enum(['people', 'groups', 'all_eligible']),
    personIds: z.array(identifierSchema).default([]),
    groupIds: z.array(identifierSchema).default([]),
    includeNestedGroups: z.boolean().default(true),
    requiredProfileType: businessProfileTypeSchema.default('meal_recipient'),
})
    .superRefine((value, context) => {
    if (value.mode === 'people' && value.personIds.length === 0)
        context.addIssue({
            code: 'custom',
            message: 'Wskaż co najmniej jedną osobę.',
            path: ['personIds'],
        });
    if (value.mode === 'groups' && value.groupIds.length === 0)
        context.addIssue({
            code: 'custom',
            message: 'Wskaż co najmniej jedną grupę.',
            path: ['groupIds'],
        });
});
export const directoryAudiencePersonSchema = z.object({
    personId: identifierSchema,
    displayName: z.string().min(1).max(200),
    groupIds: z.array(identifierSchema),
    updatedAt: isoDateTimeSchema,
});
export const directoryAudienceSnapshotSchema = z.object({
    institutionId: identifierSchema,
    resolvedAt: isoDateTimeSchema,
    snapshotHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    people: z.array(directoryAudiencePersonSchema),
});
/** Deliberately minimal projection for the Communication recipient picker. */
export const directoryRecipientSearchQuerySchema = z.object({
    phrase: z.string().trim().min(2).max(80),
    limit: z.coerce.number().int().min(1).max(20).default(10),
});
export const directoryRecipientSchema = z.object({
    personId: identifierSchema,
    displayName: z.string().min(1).max(200),
});
export const directoryRecipientSearchSchema = z.object({
    items: z.array(directoryRecipientSchema).max(20),
});
export const provisioningResultSchema = userDetailsSchema.extend({
    activation: z.object({
        delivery: z.literal('email'),
        expiresAt: isoDateTimeSchema,
        adminCode: z.string().min(8).max(32),
        previewToken: z.string().min(32).optional(),
    }),
});
export const federatedSad2UserResultSchema = userDetailsSchema.extend({
    legacySubject: z.string().regex(/^sad2:[1-9][0-9]*$/),
});
export const activationStatusSchema = z.object({
    valid: z.boolean(),
    displayName: z.string().min(1).max(200).optional(),
    expiresAt: isoDateTimeSchema.optional(),
});
export const completeActivationSchema = z
    .object({
    token: z.string().min(8).max(200),
    password: z
        .string()
        .min(12)
        .max(128)
        .regex(/[a-z]/)
        .regex(/[A-Z]/)
        .regex(/[0-9]/)
        .regex(/[^A-Za-z0-9]/),
    passwordConfirmation: z.string().min(1),
})
    .refine((value) => value.password === value.passwordConfirmation, {
    message: 'Hasła muszą być identyczne.',
    path: ['passwordConfirmation'],
});
export const createUserSchema = z.object({
    email: z.email(),
    givenName: z.string().trim().min(1).max(100),
    familyName: z.string().trim().min(1).max(100),
    locale: z.string().min(2).max(20).default('pl-PL'),
    groupIds: z.array(identifierSchema).default([]),
    profileTypes: z.array(businessProfileTypeSchema).min(1).default(['basic']),
});
/** A ready SAD5 account linked to a SAD2 password federation. */
export const importFederatedSad2UserSchema = createUserSchema.extend({
    legacySubject: z.string().regex(/^sad2:[1-9][0-9]*$/),
});
export const updateUserSchema = createUserSchema
    .omit({ email: true, profileTypes: true })
    .partial()
    .extend({ revision: z.number().int().positive() });
export const accountTransitionSchema = z.object({
    targetState: z.enum(['active', 'blocked', 'suspended', 'expired', 'closed']),
    reason: z.string().trim().min(3).max(500),
});
export const groupSchema = z.lazy(() => z.object({
    id: identifierSchema,
    name: z.string().min(1).max(160),
    description: z.string().max(500).nullable(),
    parentId: identifierSchema.nullable(),
    memberCount: z.number().int().nonnegative(),
    mfaTrustHours: z
        .number()
        .int()
        .min(0)
        .max(24 * 365),
    revision: z.number().int().positive(),
    children: z.array(groupSchema),
}));
export const createGroupSchema = z.object({
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(500).nullable().default(null),
    parentId: identifierSchema.nullable().default(null),
    mfaTrustHours: z
        .number()
        .int()
        .min(0)
        .max(24 * 365)
        .default(720),
});
export const updateGroupSchema = createGroupSchema.partial().extend({
    revision: z.number().int().positive(),
});
export const assignGroupMembershipSchema = z
    .object({
    groupId: identifierSchema,
    validFrom: isoDateTimeSchema.nullable().default(null),
    validUntil: isoDateTimeSchema.nullable().default(null),
})
    .refine((value) => !value.validFrom ||
    !value.validUntil ||
    new Date(value.validUntil) > new Date(value.validFrom), { message: 'Koniec członkostwa musi być późniejszy od początku.' });
export const removeGroupMembershipSchema = z.object({
    reason: z.string().trim().min(3).max(500),
});
export const permissionEffectSchema = z.enum(['allow', 'deny']);
export const permissionOperationIdSchema = z
    .string()
    .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/);
export const permissionOperationDefinitionSchema = z.object({
    id: permissionOperationIdSchema,
    module: z.string().regex(/^[a-z][a-z0-9-]*$/),
    moduleLabel: z.string().min(1).max(80),
    label: z.string().min(1).max(160),
    description: z.string().min(1).max(500),
});
/**
 * Public manifests of grantable operations. A product owns the entries in its
 * module section; IAM is the single reader and validator of the aggregate.
 * New product integrations extend this contract rather than accepting a free
 * text operation from an administrator.
 */
export const permissionOperationCatalog = [
    {
        id: 'iam.personnotes.create',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Dodawanie notatek administracyjnych',
        description: 'Pozwala dodawać prywatne notatki do karty osoby.',
    },
    {
        id: 'iam.personnotes.edit',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Edycja notatek administracyjnych',
        description: 'Pozwala tworzyć kolejne wersje prywatnych notatek osoby.',
    },
    {
        id: 'iam.personnotes.read',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Odczyt notatek administracyjnych',
        description: 'Pozwala czytać prywatne notatki administracyjne osoby.',
    },
    {
        id: 'iam.access.explain',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Wyjaśnianie dostępu',
        description: 'Pozwala sprawdzać, dlaczego konto ma lub nie ma dostępu.',
    },
    {
        id: 'iam.audit.read',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Odczyt audytu',
        description: 'Pozwala przeglądać dziennik audytu placówki.',
    },
    {
        id: 'iam.groups.read',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Odczyt grup',
        description: 'Pozwala przeglądać strukturę grup i podgrup.',
    },
    {
        id: 'iam.groups.write',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Zarządzanie grupami',
        description: 'Pozwala tworzyć i zmieniać grupy oraz członkostwa.',
    },
    {
        id: 'iam.permissions.read',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Odczyt reguł dostępu',
        description: 'Pozwala przeglądać katalog i reguły uprawnień grup.',
    },
    {
        id: 'iam.permissions.write',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Zarządzanie regułami dostępu',
        description: 'Pozwala nadawać i wycofywać reguły uprawnień grup.',
    },
    {
        id: 'iam.sync.read',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Odczyt synchronizacji',
        description: 'Pozwala odczytywać strumień zmian IAM.',
    },
    {
        id: 'iam.users.block',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Blokowanie kont',
        description: 'Pozwala blokować konto i wycofywać jego sesje.',
    },
    {
        id: 'iam.users.contact.read',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Odczyt danych kontaktowych',
        description: 'Pozwala zobaczyć dane kontaktowe użytkownika.',
    },
    {
        id: 'iam.users.read',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Odczyt użytkowników',
        description: 'Pozwala przeglądać użytkowników placówki.',
    },
    {
        id: 'iam.users.security.read',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Odczyt danych bezpieczeństwa',
        description: 'Pozwala zobaczyć dane bezpieczeństwa konta.',
    },
    {
        id: 'iam.users.write',
        module: 'iam',
        moduleLabel: 'Tożsamość i dostęp',
        label: 'Zarządzanie użytkownikami',
        description: 'Pozwala tworzyć i zmieniać lokalne konta oraz profile.',
    },
    {
        id: 'workflow.admin',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Administrator workflow',
        description: 'Pozwala wykonywać wszystkie operacje modułu Workflow.',
    },
    {
        id: 'workflow.audit.read',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Odczyt historii spraw',
        description: 'Pozwala przeglądać historię spraw workflow.',
    },
    {
        id: 'workflow.case.create',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Tworzenie spraw',
        description: 'Pozwala rozpoczynać sprawy workflow.',
    },
    {
        id: 'workflow.case.read',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Odczyt spraw',
        description: 'Pozwala przeglądać sprawy workflow.',
    },
    {
        id: 'workflow.case.transition',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Zmiana stanu sprawy',
        description: 'Pozwala wykonywać przejścia stanu sprawy.',
    },
    {
        id: 'workflow.decision.record',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Rejestrowanie decyzji',
        description: 'Pozwala zapisywać decyzje w sprawie.',
    },
    {
        id: 'workflow.definition.manage',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Zarządzanie definicjami',
        description: 'Pozwala tworzyć i publikować definicje workflow.',
    },
    {
        id: 'workflow.migration.run',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Uruchamianie migracji',
        description: 'Pozwala uruchamiać migracje spraw workflow.',
    },
    {
        id: 'workflow.task.assign',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Przydzielanie zadań',
        description: 'Pozwala przydzielać zadania workflow.',
    },
    {
        id: 'workflow.task.claim',
        module: 'workflow',
        moduleLabel: 'Workflow',
        label: 'Podejmowanie zadań',
        description: 'Pozwala podejmować zadania workflow.',
    },
    {
        id: 'communication.admin',
        module: 'communication',
        moduleLabel: 'Komunikacja',
        label: 'Administrator komunikacji',
        description: 'Pozwala wykonywać wszystkie operacje modułu Komunikacja.',
    },
    {
        id: 'communication.critical_access',
        module: 'communication',
        moduleLabel: 'Komunikacja',
        label: 'Dostęp krytyczny do komunikacji',
        description: 'Pozwala uzyskać audytowany dostęp krytyczny po reautoryzacji.',
    },
    {
        id: 'communication.message.read',
        module: 'communication',
        moduleLabel: 'Komunikacja',
        label: 'Odczyt komunikacji',
        description: 'Pozwala przeglądać komunikację w dozwolonym zakresie.',
    },
    {
        id: 'communication.message.send',
        module: 'communication',
        moduleLabel: 'Komunikacja',
        label: 'Wysyłanie komunikacji',
        description: 'Pozwala tworzyć i wysyłać komunikaty.',
    },
    {
        id: 'communication.queue.claim',
        module: 'communication',
        moduleLabel: 'Komunikacja',
        label: 'Podejmowanie kolejki komunikacji',
        description: 'Pozwala podejmować pozycje kolejki komunikacji.',
    },
    {
        id: 'canteen.kitchen.manage',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Zarządzanie kuchnią i magazynem',
        description: 'Pozwala zarządzać produktami, produkcją, magazynem i bezpieczeństwem żywności.',
    },
    {
        id: 'canteen.dietary.manage',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Zarządzanie profilami żywieniowymi',
        description: 'Pozwala przeglądać i aktualizować profile oraz ograniczenia żywieniowe.',
    },
    {
        id: 'canteen.reports.read',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Odczyt raportów stołówki',
        description: 'Pozwala przeglądać raporty operacyjne i audytowe stołówki.',
    },
    {
        id: 'canteen.administration.manage',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Zarządzanie stołówką',
        description: 'Pozwala zarządzać konfiguracją, okresami, menu i rezerwacjami masowymi stołówki.',
    },
    {
        id: 'canteen.terminal.configure',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Konfiguracja terminali',
        description: 'Pozwala rejestrować czytniki oraz przygotowywać i synchronizować pakiety offline.',
    },
    {
        id: 'canteen.terminal.issue',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Wydawanie posiłków',
        description: 'Pozwala obsługiwać terminal i potwierdzać wydania posiłków.',
    },
    {
        id: 'canteen.terminal.correct',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Korekta wydania posiłku',
        description: 'Pozwala odwracać wcześniej potwierdzone wydania posiłków.',
    },
    {
        id: 'canteen.documents.issue',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Wystawianie dokumentów',
        description: 'Pozwala wystawiać dokumenty stołówki.',
    },
    {
        id: 'canteen.documents.read',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Odczyt dokumentów',
        description: 'Pozwala przeglądać dokumenty stołówki.',
    },
    {
        id: 'canteen.documents.template.write',
        module: 'canteen',
        moduleLabel: 'Stołówka',
        label: 'Zarządzanie szablonami dokumentów',
        description: 'Pozwala zmieniać szablony dokumentów stołówki.',
    },
];
export function findPermissionOperation(operation) {
    return permissionOperationCatalog.find((item) => item.id === operation);
}
import { personDirectoryGrantScopeSchema } from './person-directory-scope.js';
export * from './person-directory-scope.js';
export const permissionGrantSchema = z.object({
    id: identifierSchema,
    groupId: identifierSchema,
    operation: permissionOperationIdSchema,
    effect: permissionEffectSchema,
    status: z.enum(['active', 'revoked']),
    scope: z.record(z.string(), z.unknown()).default({}),
    validFrom: isoDateTimeSchema.nullable(),
    validUntil: isoDateTimeSchema.nullable(),
    revision: z.number().int().positive(),
});
export const createPermissionGrantSchema = z
    .object({
    operation: permissionOperationIdSchema,
    effect: permissionEffectSchema,
    scope: z.record(z.string(), z.unknown()).default({}),
    validFrom: isoDateTimeSchema.nullable().default(null),
    validUntil: isoDateTimeSchema.nullable().default(null),
})
    .refine((value) => !value.validFrom ||
    !value.validUntil ||
    new Date(value.validUntil) > new Date(value.validFrom), { message: 'Koniec reguły musi być późniejszy od początku.' })
    .refine((value) => value.operation !== 'iam.users.read' ||
    personDirectoryGrantScopeSchema.safeParse(value.scope).success, { message: 'Niepoprawny zakres katalogu osób.', path: ['scope'] });
export const revokePermissionGrantSchema = z.object({
    reason: z.string().trim().min(3).max(500),
    revision: z.number().int().positive(),
});
export const accessExplanationSchema = z.object({
    operation: z.string(),
    allowed: z.boolean(),
    decisiveRule: permissionGrantSchema.nullable(),
    evaluatedGroupIds: z.array(identifierSchema),
    reason: z.string(),
});
export const auditEventSchema = z.object({
    id: identifierSchema,
    occurredAt: isoDateTimeSchema,
    actorId: identifierSchema.nullable(),
    action: z.string(),
    subjectType: z.string(),
    subjectId: z.string(),
    correlationId: z.string(),
    outcome: z.enum(['success', 'denied', 'failure']),
    summary: z.string(),
});
export const syncEnvelopeSchema = z.object({
    cursor: z.string(),
    serverTime: isoDateTimeSchema,
    changes: z.array(z.object({
        id: identifierSchema,
        entity: z.enum(['user', 'group', 'permission', 'session']),
        entityId: identifierSchema,
        operation: z.enum(['upsert', 'delete', 'revoke']),
        revision: z.number().int().positive(),
        payload: z.record(z.string(), z.unknown()).nullable(),
    })),
});
export const apiErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    correlationId: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
});
